import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { useTheme, setTheme, type Theme } from "../theme";
import type { AppSettings, Usage, Me } from "../types";
import GenerationLoader from "../components/GenerationLoader";
import DeleteAccountModal from "../components/DeleteAccountModal";

const PLAN_NAME: Record<string, string> = { trial: "Trial", pratica: "Pratica", maestro: "Maestro" };

const VOICES = ["Aoede", "Puck", "Charon", "Kore", "Fenrir"];
const LEVELS = ["B1", "B2", "C1", "C2"] as const;

/** Autosave posts the whole form, so an empty/out-of-range number field would 422
 *  the request mid-typing. Clamp on change instead. */
const clamp = (v: string, min: number, max: number, fallback: number) => {
  const n = Number(v);
  return Number.isFinite(n) && v !== "" ? Math.min(max, Math.max(min, Math.round(n))) : fallback;
};

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-6 sm:p-8">
      <p className="section-label mb-6">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  wide,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-2.5 ${wide ? "sm:col-span-2" : ""}`}>
      <div>
        <label className="text-sm font-semibold text-ink">{label}</label>
        {hint && <p className="text-xs text-text-tertiary mt-1 leading-relaxed max-w-prose">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 cursor-pointer ${
        checked ? "bg-ink" : "bg-surface2"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-cream transition-transform duration-200 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

export default function SettingsPage() {
  const [theme] = useTheme();
  const [form, setForm] = useState<AppSettings>({
    memory_sessions_n: 2,
    voice_name: "Aoede",
    tutor_level: "C1",
    preferred_topics: "",
    exercise_generation_cap: 40,
    elena_greets_first: true,
    session_max_minutes: 10,
    live_error_logging: "all",
    session_engine: "live",
    cascade_pronunciation_pass: false,
    mic_mode: "manual",
    mobile_sessions_enabled: false,
    practice_schedule: { enabled: false, days: [], time: "18:00", reminders: true, reminder_minutes_before: 0 },
  });
  const [status, setStatus] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [billingBusy, setBillingBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.getSettings()
      .then(setForm)
      .catch(() => setStatus({ type: "error", msg: "Failed to load settings." }))
      .finally(() => setLoading(false));
    api.getUsage().then(setUsage).catch(() => { /* plan card omitted */ });
    api.getMe().then(setMe).catch(() => { /* admin-only section stays hidden */ });
  }, []);

  // Returned from a Session Pack checkout: confirm + refetch (webhook is async).
  useEffect(() => {
    const s = searchParams.get("status");
    if (s !== "pack_success" && s !== "pack_cancel") return;
    if (s === "pack_success") {
      setStatus({ type: "success", msg: "Session pack added — credits will appear in a moment." });
      const poll = (n: number) => {
        api.getUsage().then((u) => {
          setUsage(u);
          if (!u.pack_credits && n < 5) setTimeout(() => poll(n + 1), 2000);
        }).catch(() => {});
      };
      poll(0);
    }
    searchParams.delete("status");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const buyPack = async () => {
    setBillingBusy(true);
    setStatus(null);
    try {
      const { url } = await api.buySessionPack("/settings");
      window.location.href = url;
    } catch (e) {
      setStatus({ type: "error", msg: e instanceof ApiError ? e.message : "Could not start checkout." });
      setBillingBusy(false);
    }
  };

  const manageBilling = async () => {
    setBillingBusy(true);
    setStatus(null);
    try {
      const { url } = await api.openBillingPortal("/settings");
      window.location.href = url;
    } catch (e) {
      setStatus({ type: "error", msg: e instanceof ApiError ? e.message : "Could not open the billing portal." });
      setBillingBusy(false);
    }
  };

  // Autosave — no Save button. Each edit is persisted after a short debounce so
  // typing in the topics box doesn't fire one request per keystroke. savedRef holds
  // the last persisted form (also the baseline set right after the initial load, so
  // mounting never triggers a write); a failed save leaves it stale, so the next
  // edit retries the whole form.
  const savedRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    const payload = JSON.stringify(form);
    if (savedRef.current === null) { savedRef.current = payload; return; }
    if (savedRef.current === payload) return;
    const t = setTimeout(async () => {
      try {
        await api.updateSettings(form);
        savedRef.current = payload;
        setStatus({ type: "success", msg: "Saved — applies to your next session." });
      } catch {
        setStatus({ type: "error", msg: "Failed to save settings." });
      }
    }, 600);
    return () => clearTimeout(t);
  }, [form, loading]);

  // Fade the "Saved" confirmation out; errors stay until the next save attempt.
  useEffect(() => {
    if (status?.type !== "success") return;
    const t = setTimeout(() => setStatus(null), 2500);
    return () => clearTimeout(t);
  }, [status]);

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <GenerationLoader
        messages={["Loading your settings...", "Fetching your preferences..."]}
        subtitle=""
      />
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto w-full px-5 sm:px-8 py-10">
        <div className="flex flex-col gap-4 animate-rise [animation-delay:0.05s]">

        <header className="mb-4 animate-rise flex items-start justify-between gap-4">
          <div>
            <h1>Settings</h1>
            <p className="text-text-tertiary text-[0.9375rem] mt-1">Shape how Elena teaches you. Changes save automatically.</p>
          </div>
          <div className="flex items-center gap-3 pt-1 min-h-[2.25rem]">
            {status && (
              <p className={`text-sm font-medium ${status.type === "success" ? "text-success" : "text-error"}`}>
                {status.msg}
              </p>
            )}
          </div>
        </header>

          {/* ── Voice & Session ─────────────────────────────── */}
          <SectionCard title="Voice & Session">
            <Field label="Level" hint="Elena adapts her vocabulary and pace to this.">
              <div className="flex gap-1 bg-surface2 rounded-full p-1 w-fit">
                {LEVELS.map((l) => (
                  <button
                    type="button"
                    key={l}
                    onClick={() => setForm({ ...form, tutor_level: l })}
                    className={`px-5 py-1.5 rounded-full text-[0.8125rem] font-medium transition-colors duration-200 cursor-pointer ${
                      form.tutor_level === l ? "bg-ink text-cream" : "text-text-secondary hover:text-ink"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Session length" hint="Around this mark Elena wraps up and ends the session herself.">
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={form.session_max_minutes}
                  onChange={(e) => setForm({ ...form, session_max_minutes: Number(e.target.value) })}
                  className="flex-1 accent-ink cursor-pointer"
                />
                <span className="text-sm text-text-tertiary w-20 text-right">{form.session_max_minutes} min</span>
              </div>
            </Field>

            <Field label="Elena starts the conversation" hint="Elena greets you and kicks off each session instead of waiting.">
              <Toggle
                checked={form.elena_greets_first}
                onChange={() => setForm({ ...form, elena_greets_first: !form.elena_greets_first })}
              />
            </Field>

            <Field label="Mic mode" hint='Auto detects silence and sends automatically. Manual requires you to tap the mic button to send each turn.'>
              <div className="flex gap-1 bg-surface2 rounded-full p-1 w-fit">
                {(["manual", "auto"] as const).map((m) => (
                  <button
                    type="button"
                    key={m}
                    onClick={() => setForm({ ...form, mic_mode: m })}
                    className={`px-5 py-1.5 rounded-full text-[0.8125rem] font-medium transition-colors duration-200 cursor-pointer capitalize ${
                      form.mic_mode === m ? "bg-ink text-cream" : "text-text-secondary hover:text-ink"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </Field>
          </SectionCard>

          {/* ── Analysis & Cost (admin only) ─────────────────── */}
          {me?.is_admin && (
          <SectionCard title="Analysis & Cost">
            <Field label="Session memory" hint="How many past sessions Elena remembers when you talk.">
              <input
                type="number"
                min={1}
                max={20}
                value={form.memory_sessions_n}
                onChange={(e) => setForm({ ...form, memory_sessions_n: clamp(e.target.value, 1, 20, 1) })}
                className="input-base w-24 text-center"
              />
            </Field>

            <Field label="Elena's voice">
              <select
                value={form.voice_name}
                onChange={(e) => setForm({ ...form, voice_name: e.target.value })}
                className="input-base w-full sm:w-48"
              >
                {VOICES.map((v) => <option key={v} className="bg-surface text-text">{v}</option>)}
              </select>
            </Field>

            <Field label="Exercise cap" hint="Max exercises generated per 'Generate new' run.">
              <input
                type="number"
                min={1}
                max={200}
                value={form.exercise_generation_cap}
                onChange={(e) => setForm({ ...form, exercise_generation_cap: clamp(e.target.value, 1, 200, 1) })}
                className="input-base w-24 text-center"
              />
            </Field>

            <Field
              label="Live error logging"
              hint="Pronunciation-only keeps audio-dependent feedback live and leaves grammar, vocabulary, and syntax to the post-session analysis."
            >
              <div className="flex gap-1 bg-surface2 rounded-full p-1 w-fit">
                {([['all', 'All errors'], ['pronunciation_only', 'Pronunciation only']] as const).map(([val, label]) => (
                  <button
                    type="button"
                    key={val}
                    onClick={() => setForm({ ...form, live_error_logging: val })}
                    className={`px-4 py-1.5 rounded-full text-[0.8125rem] font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      form.live_error_logging === val ? "bg-ink text-cream" : "text-text-secondary hover:text-ink"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>

            <Field
              label="Voice engine"
              hint='"Premium" uses Gemini Live with real-time pronunciation feedback. "Regular" is 75–90% cheaper: local STT, a text model, and Gemini speech.'
            >
              <div className="flex gap-1 bg-surface2 rounded-full p-1 w-fit">
                {([["live", "Premium"], ["cascaded", "Regular"]] as const).map(([val, label]) => (
                  <button
                    type="button"
                    key={val}
                    onClick={() => setForm({ ...form, session_engine: val })}
                    className={`px-4 py-1.5 rounded-full text-[0.8125rem] font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      form.session_engine === val ? "bg-ink text-cream" : "text-text-secondary hover:text-ink"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>

            {form.session_engine === "cascaded" && (
              <Field
                label="Pronunciation pass"
                hint="Runs a quick audio check after the session to catch pronunciation errors the Regular engine can't hear live."
              >
                <Toggle
                  checked={form.cascade_pronunciation_pass}
                  onChange={() => setForm({ ...form, cascade_pronunciation_pass: !form.cascade_pronunciation_pass })}
                />
              </Field>
            )}

            <Field
              label="Mobile practice sessions"
              hint="Off for everyone by default: phone speaker/mic echo and tab suspension break the turn-taking loop. Enable to test voice sessions on a touch device."
            >
              <Toggle
                checked={form.mobile_sessions_enabled}
                onChange={() => setForm({ ...form, mobile_sessions_enabled: !form.mobile_sessions_enabled })}
              />
            </Field>

          </SectionCard>
          )}

          {/* ── Plan ─────────────────────────────────────────── */}
          {usage && (
            <SectionCard title="Plan">
              <Field label="Current plan" hint="Your plan sets your live voice engine, error feedback, and session allowance.">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full bg-accent/15 text-accent text-sm font-semibold uppercase tracking-wide">
                    {PLAN_NAME[usage.plan] ?? usage.plan}
                  </span>
                </div>
              </Field>
              <Field label="Sessions" hint={usage.window === "trial" ? "Trial: 3 sessions, no time limit." : "Resets at the start of each month."}>
                <div className="text-sm text-ink">
                  {usage.window === "trial" ? (
                    <span>{usage.remaining} of {usage.limit} left</span>
                  ) : (
                    <span>{usage.used} of {usage.limit} used this month</span>
                  )}
                  {usage.pack_credits ? (
                    <span className="text-accent font-medium"> · +{usage.pack_credits} pack credit{usage.pack_credits === 1 ? "" : "s"}</span>
                  ) : null}
                  {usage.blocked && usage.reason && (
                    <p className="text-xs text-rose-400 mt-1">{usage.reason}</p>
                  )}
                </div>
              </Field>
              <Field label="Manage plan" hint="Upgrade, switch plan, or top up with a session pack." wide>
                <div className="flex flex-wrap gap-2">
                  {usage.plan !== "maestro" && (
                    <button type="button" onClick={() => navigate("/upgrade")} className="btn-primary">
                      {usage.plan === "trial" ? "Upgrade" : "Upgrade plan"}
                    </button>
                  )}
                  {usage.plan !== "trial" && (
                    <>
                      <button type="button" onClick={buyPack} disabled={billingBusy} className="btn-quiet">
                        Buy session pack
                      </button>
                      <button type="button" onClick={manageBilling} disabled={billingBusy} className="btn-quiet">
                        Manage billing
                      </button>
                    </>
                  )}
                </div>
              </Field>
              <Field label="Error feedback" hint="What Elena flags while you speak (the rest is analysed after each session).">
                <span className="text-sm text-ink">
                  {form.live_error_logging === "all" ? "All error types (live)" : "Pronunciation only (live)"}
                </span>
              </Field>
              <Field label="Study materials" hint="Uploading your own notes/articles/videos to the Library.">
                <span className="text-sm text-ink">{usage.plan === "maestro" ? "Included" : "Maestro only"}</span>
              </Field>
            </SectionCard>
          )}

          {/* ── Personalization ──────────────────────────────── */}
          <SectionCard title="Personalization">
            <Field label="Preferred topics" hint="Optional hints — Elena will steer the conversation toward these." wide>
              <textarea
                value={form.preferred_topics}
                placeholder="e.g. travel, food, Italian cinema, grammar drills…"
                onChange={(e) => setForm({ ...form, preferred_topics: e.target.value })}
                className="input-base resize-y min-h-[100px] leading-normal rounded-[10px] w-full"
              />
            </Field>
          </SectionCard>

          {/* ── Appearance ─────────────────────────────────────── */}
          <SectionCard title="Appearance">
            <Field label="Theme" hint="Switch between dark and light mode.">
              <div className="flex gap-1 bg-surface2 rounded-full p-1 w-fit">
                {(["dark", "light"] as Theme[]).map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setTheme(t)}
                    className={`px-5 py-1.5 rounded-full text-[0.8125rem] font-medium transition-colors duration-200 cursor-pointer capitalize ${
                      theme === t ? "bg-ink text-cream" : "text-text-secondary hover:text-ink"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </Field>
          </SectionCard>

          {/* ── Danger Zone ────────────────────────────────────── */}
          {me && (
            <div className="card p-6 sm:p-8 border-error/40">
              <p className="section-label mb-6 text-error">Danger Zone</p>
              <Field
                label="Delete my account"
                hint="Permanently erases your account and everything in it: sessions, transcripts, progress, vocabulary, exercises, uploaded materials, and settings. Any subscription is canceled with no refund. This cannot be undone."
                wide
              >
                <div>
                  <button type="button" onClick={() => setDeleteOpen(true)} className="btn-quiet !text-error !border-error/50">
                    Delete my account…
                  </button>
                </div>
              </Field>
            </div>
          )}

        </div>
      </div>
      {deleteOpen && me && (
        <DeleteAccountModal email={me.email} plan={me.plan} onClose={() => setDeleteOpen(false)} />
      )}
    </div>
  );
}
